import React from 'react';
import { Search, User, BookOpen, Home, Bookmark, Play, ArrowRight, Star, Clock } from 'lucide-react';

// Enhanced Home Section with logo, welcome message and more customized content
const HomeSection = ({ 
  bookTitles = [], 
  appName = "BookMind", 
  primaryColor, 
  secondaryColor,
  logoUrl,
  welcomeMessage,
  appDescription,
  splashVideo
}) => (
  <div className="flex flex-col">
    {/* App header with logo */}
    <div 
      className="px-4 py-3 flex items-center"
      style={{ backgroundColor: primaryColor }}
    >
      <div className="flex items-center">
        {logoUrl ? (
          <img src={logoUrl} alt={`${appName} logo`} className="h-8 w-8 mr-2 rounded" />
        ) : (
          <div 
            className="h-8 w-8 rounded flex items-center justify-center text-white font-bold mr-2"
            style={{ backgroundColor: secondaryColor || '#f97316' }}
          >
            {appName.substring(0, 1).toUpperCase()}
          </div>
        )}
        <h1 className="text-white font-bold text-lg">{appName}</h1>
      </div>
    </div>
    
    {/* Splash video or welcome banner */}
    {splashVideo ? (
      <div className="relative">
        <div className="aspect-video bg-gray-900 relative overflow-hidden">
          {splashVideo.thumbnail ? (
            <img 
              src={splashVideo.thumbnail} 
              alt={splashVideo.title || "Welcome"} 
              className="w-full h-full object-cover opacity-60"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br" style={{ 
              background: `linear-gradient(to bottom right, ${primaryColor}, ${secondaryColor || '#f97316'})` 
            }}></div>
          )}
          <div className="absolute inset-0 flex items-center justify-center">
            <div 
              className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center"
              style={{ backgroundColor: `${secondaryColor}40` || 'rgba(255,255,255,0.2)' }}
            >
              <Play 
                className="w-8 h-8 text-white ml-1" 
                fill="white"
              />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <h2 className="text-white font-bold">{splashVideo.title || welcomeMessage || `Welcome to ${appName}`}</h2>
            <p className="text-white/80 text-sm">{appDescription || "Tap to play introduction"}</p>
          </div>
        </div>
      </div>
    ) : (
      <div 
        className="p-4 bg-gradient-to-r" 
        style={{ 
          background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor || primaryColor})` 
        }}
      >
        <h2 className="text-white text-xl font-bold">{welcomeMessage || `Welcome to ${appName}`}</h2>
        <p className="text-white/90 mt-1">{appDescription || "Your personalized reading experience"}</p>
      </div>
    )}
    
    {/* Main content */}
    <div className="p-4">
      {/* Featured Books */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold">Latest Books</h3>
          <button 
            className="text-sm font-medium flex items-center"
            style={{ color: primaryColor }}
          >
            See All <ArrowRight className="w-3 h-3 ml-1" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {(bookTitles || []).slice(0, 4).map((book, index) => (
            <div key={index} className="bg-white rounded-lg shadow overflow-hidden">
              <div 
                className="h-32 flex items-center justify-center bg-gray-100"
                style={{ backgroundColor: book.cover ? 'white' : '#f3f4f6' }}
              >
                {book.cover ? (
                  <img 
                    src={book.cover} 
                    alt={book.title} 
                    className="h-full object-contain"
                  />
                ) : (
                  <div 
                    className="w-16 h-24 rounded flex items-center justify-center text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <BookOpen className="w-8 h-8" />
                  </div>
                )}
              </div>
              <div className="p-2">
                <h4 className="font-medium text-sm line-clamp-1">{book.title}</h4>
                {book.author && (
                  <p className="text-xs text-gray-500">{book.author}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Recommended Section */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold">Recommended for You</h3>
          <button 
            className="text-sm font-medium flex items-center"
            style={{ color: primaryColor }}
          >
            More <ArrowRight className="w-3 h-3 ml-1" />
          </button>
        </div>
        <div className="space-y-3">
          {(bookTitles || []).slice(0, 3).map((book, index) => (
            <div key={`rec-${index}`} className="flex bg-white rounded-lg shadow p-2">
              <div 
                className="w-16 h-20 flex-shrink-0 mr-3 flex items-center justify-center"
                style={{ backgroundColor: book.cover ? 'white' : '#f3f4f6' }}
              >
                {book.cover ? (
                  <img 
                    src={book.cover} 
                    alt={book.title} 
                    className="h-full object-contain"
                  />
                ) : (
                  <div 
                    className="w-10 h-16 rounded flex items-center justify-center text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <BookOpen className="w-5 h-5" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <h4 className="font-medium text-sm">{book.title}</h4>
                  <div className="flex items-center">
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    <span className="text-xs ml-1">{(4 + Math.random()).toFixed(1)}</span>
                  </div>
                </div>
                {book.author && (
                  <p className="text-xs text-gray-500 mb-1">{book.author}</p>
                )}
                <p className="text-xs text-gray-600 line-clamp-2">
                  {book.description || "Explore this book and discover more content like this in your personalized library."}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Enhanced Library Section with actual book lists
const LibrarySection = ({ bookTitles = [], primaryColor, secondaryColor }) => (
  <div className="flex flex-col h-full">
    <div 
      className="px-4 py-3"
      style={{ backgroundColor: primaryColor }}
    >
      <h1 className="text-white font-bold text-lg">Your Library</h1>
    </div>
    
    <div className="p-4">
      <div className="flex space-x-3 mb-6 overflow-x-auto pb-2">
        <div 
          className="px-3 py-1 rounded-full text-sm font-medium text-white"
          style={{ backgroundColor: primaryColor }}
        >
          All
        </div>
        <div className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-700">
          Books
        </div>
        <div className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-700">
          Articles
        </div>
        <div className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-700">
          Audiobooks
        </div>
        <div className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-700">
          Favorites
        </div>
      </div>
      
      <h3 className="text-lg font-semibold mb-3">Recently Added</h3>
      <div className="space-y-3">
        {(bookTitles || []).map((book, index) => (
          <div key={`lib-${index}`} className="flex bg-white rounded-lg shadow p-3">
            <div 
              className="w-16 h-20 flex-shrink-0 mr-3 flex items-center justify-center"
              style={{ backgroundColor: book.cover ? 'white' : '#f3f4f6' }}
            >
              {book.cover ? (
                <img 
                  src={book.cover} 
                  alt={book.title} 
                  className="h-full object-contain"
                />
              ) : (
                <div 
                  className="w-10 h-16 rounded flex items-center justify-center text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  <BookOpen className="w-5 h-5" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-sm">{book.title}</h4>
                  {book.author && (
                    <p className="text-xs text-gray-500">{book.author}</p>
                  )}
                </div>
                <button className="p-1 rounded-full bg-gray-100 text-gray-500">
                  <Bookmark className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center text-xs text-gray-500">
                  <Clock className="w-3 h-3 mr-1" />
                  <span>12 min read</span>
                </div>
                <div 
                  className="px-2 py-1 rounded-full text-xs font-medium"
                  style={{ 
                    backgroundColor: `${primaryColor}20`,
                    color: primaryColor
                  }}
                >
                  Continue Reading
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Enhanced Generic Section with more customization
const GenericSection = ({ 
  icon: Icon, 
  title, 
  message, 
  primaryColor,
  secondaryColor,
  appName
}) => (
  <div className="flex flex-col h-full">
    <div 
      className="px-4 py-3"
      style={{ backgroundColor: primaryColor }}
    >
      <h1 className="text-white font-bold text-lg">{title}</h1>
    </div>
    
    <div className="flex flex-col items-center justify-center flex-1 p-4">
      <div 
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
        style={{ 
          backgroundColor: `${primaryColor}20`,
          border: `2px solid ${primaryColor}`
        }}
      >
        <Icon size={32} style={{ color: primaryColor }} />
      </div>
      <h2 className="text-xl font-semibold mb-3 text-center">{title}</h2>
      <p className="text-center text-gray-600 max-w-xs mb-6">
        {message}
      </p>
      <button
        className="px-4 py-2 rounded-md text-white font-medium"
        style={{ backgroundColor: primaryColor }}
      >
        Get Started
      </button>
    </div>
  </div>
);

/**
 * ContentArea component that displays different sections based on the active tab
 * Enhanced to support all customization options and screens
 */
const ContentArea = ({ 
  activeTab, 
  bookTitles = [], 
  appName = "BookMind",
  primaryColor = "#4f46e5",
  secondaryColor,
  logoUrl,
  welcomeMessage,
  appDescription,
  splashVideo,
  features = [],
  materialTheme,
  screens = []
}) => {
  // Normalize active tab to lowercase for case-insensitive comparison
  const normalizedActiveTab = activeTab.toLowerCase();
  
  return (
    <div className="h-full pb-16 overflow-y-auto bg-gray-50">
      {normalizedActiveTab === 'home' && (
        <HomeSection 
          bookTitles={bookTitles} 
          appName={appName} 
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          logoUrl={logoUrl}
          welcomeMessage={welcomeMessage}
          appDescription={appDescription}
          splashVideo={splashVideo}
        />
      )}
      
      {(normalizedActiveTab === 'library' || normalizedActiveTab === 'books') && (
        <LibrarySection 
          bookTitles={bookTitles} 
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      )}
      
      {normalizedActiveTab === 'search' && (
        <GenericSection 
          icon={Search} 
          title="Search Content" 
          message="Find books, videos, and other content in your personalized library with powerful search capabilities."
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          appName={appName}
        />
      )}
      
      {(normalizedActiveTab === 'profile' || normalizedActiveTab === 'account') && (
        <GenericSection 
          icon={User} 
          title="Your Profile" 
          message={`Manage your ${appName} account settings, track your reading progress, and customize your experience.`}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          appName={appName}
        />
      )}
      
      {/* Handle custom screens defined from OpenAI */}
      {screens.length > 0 && !['home', 'library', 'books', 'search', 'profile', 'account'].includes(normalizedActiveTab) && (
        <GenericSection 
          icon={screens.find(s => s.id === normalizedActiveTab)?.icon || Home}
          title={screens.find(s => s.id === normalizedActiveTab)?.name || "Custom Screen"}
          message={screens.find(s => s.id === normalizedActiveTab)?.description || `This is a custom screen in your ${appName} app.`}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          appName={appName}
        />
      )}
    </div>
  );
};

export default ContentArea; 